import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Wrench, ArrowRight, Globe, Truck, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return `${BACKEND_URL}${src}`;
};

// IDs hand-picked to showcase variety across categories
const FEATURED_IDS = ['TL006', 'TL024', 'TL037', 'TL032', 'TL046', 'TL052'];

const FeaturedTools = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/tools/`)
      .then((res) => {
        if (!mounted) return;
        const all = res.data || [];
        const featured = FEATURED_IDS.map((id) => all.find((t) => t.id === id)).filter(Boolean);
        setTools(featured.length ? featured : all.slice(0, 6));
      })
      .catch(() => mounted && setTools([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      id="tienda-destacada"
      data-testid="featured-tools-section"
      className="relative py-20 bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-96 h-96 bg-red-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-slate-100 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <Wrench size={14} /> Nueva tienda online
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            Herramientas profesionales para{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-700">
              tus proyectos de drywall
            </span>
          </h2>
          <p className="text-lg text-slate-600">
            Más de 50 herramientas seleccionadas por expertos. Envío internacional con cotización
            personalizada para tu país.
          </p>
        </div>

        {/* Feature pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <div className="flex items-center gap-2 bg-white shadow-sm px-4 py-2 rounded-full border border-slate-200">
            <Globe size={16} className="text-red-600" />
            <span className="text-sm font-medium text-slate-700">Envío a todo el mundo</span>
          </div>
          <div className="flex items-center gap-2 bg-white shadow-sm px-4 py-2 rounded-full border border-slate-200">
            <Truck size={16} className="text-red-600" />
            <span className="text-sm font-medium text-slate-700">Cotización de envío 24-48h</span>
          </div>
          <div className="flex items-center gap-2 bg-white shadow-sm px-4 py-2 rounded-full border border-slate-200">
            <ShieldCheck size={16} className="text-red-600" />
            <span className="text-sm font-medium text-slate-700">Pago seguro con Stripe</span>
          </div>
        </div>

        {/* Tools grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent mx-auto" />
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10">
            {tools.map((tool) => (
              <Link
                key={tool.id}
                to="/tienda"
                data-testid={`featured-tool-${tool.id}`}
                className="group"
              >
                <Card className="border-0 shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden bg-white group-hover:-translate-y-1">
                  <div
                    className="aspect-[4/3] bg-slate-100 bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
                    style={{ backgroundImage: `url(${resolveImg(tool.image)})` }}
                  />
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-slate-900 text-sm sm:text-base line-clamp-2 leading-snug group-hover:text-red-600 transition-colors">
                      {tool.name}
                    </h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xl font-bold text-slate-900">
                        ${tool.price_usd?.toFixed(2)}
                      </span>
                      <span className="text-xs text-red-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        Ver más <ArrowRight size={12} />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center">
          <Link to="/tienda" data-testid="featured-tools-cta">
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
            >
              <Wrench size={20} className="mr-2 group-hover:rotate-12 transition-transform" />
              Ver todas las herramientas
              <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedTools;
